import { type UseTrackEditInCanvasSubmenuShownParams } from '@air/analytics';
import { type Clip, ClipType } from '@air/api/types';

import {
  type EditInCanvasSizeSet,
  type PendingCanvasEditRequest,
} from '~/components/v2/store/features/canvas/usePendingCanvasEditStore';

export type EditInCanvasSizeSetExposure = Pick<
  UseTrackEditInCanvasSubmenuShownParams,
  'size_set_eligible' | 'size_set_format_count' | 'size_set_id' | 'size_set_offered'
>;

export type GetEditInCanvasEligibleSizeSetParams = {
  canOfferSizeSet: boolean;
  clips: Pick<Clip, 'type'>[];
  sizeSet: EditInCanvasSizeSet | null;
};

export const getEditInCanvasEligibleSizeSet = ({
  canOfferSizeSet,
  clips,
  sizeSet,
}: GetEditInCanvasEligibleSizeSetParams): EditInCanvasSizeSet | null => {
  const [clip] = clips;

  if (!canOfferSizeSet || clips.length !== 1 || !clip || clip.type === ClipType.video) {
    return null;
  }

  return sizeSet;
};

export type GetEditInCanvasSizeSetExposureParams = {
  eligibleSizeSet: EditInCanvasSizeSet | null;
  isOffered: boolean;
};

/** Sent in both experiment arms so control has the same eligible denominator as test. */
export const getEditInCanvasSizeSetExposure = ({
  eligibleSizeSet,
  isOffered,
}: GetEditInCanvasSizeSetExposureParams): EditInCanvasSizeSetExposure => {
  if (!eligibleSizeSet) {
    return { size_set_eligible: false, size_set_offered: false };
  }

  return {
    size_set_eligible: true,
    size_set_format_count: eligibleSizeSet.formats.length,
    size_set_id: eligibleSizeSet.id,
    size_set_offered: isOffered,
  };
};

export type GetEditInCanvasOpenedSizeSetExposureParams = {
  action: PendingCanvasEditRequest['action'];
  sizeSetExposure: EditInCanvasSizeSetExposure;
};

/** `edit_in_canvas_opened` carries the set itself only when the user picked Resize for set. */
export const getEditInCanvasOpenedSizeSetExposure = ({
  action,
  sizeSetExposure,
}: GetEditInCanvasOpenedSizeSetExposureParams): EditInCanvasSizeSetExposure => {
  if (action === 'resizeForSet') {
    return sizeSetExposure;
  }

  return {
    size_set_eligible: sizeSetExposure.size_set_eligible,
    size_set_offered: sizeSetExposure.size_set_offered,
  };
};
